import { add } from 'date-fns';
import React, { FC, ReactNode, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, Card, CardBody, CardHeader } from 'reactstrap';
import { getCampaignHasFeatureEnabledDefaultOn } from 'utils/models/Performance';
import {
  ENGAGEMENT_ENTITLEMENT_WIZARD_TYPES,
  getWizardType,
  PERFORMANCE_FEATURE_INCLUDE_BASICS_PAGE,
  PHASE_TYPE_EVALUATION,
  PHASE_TYPE_OTHERS,
  PHASE_TYPE_SELF,
} from 'utils/models/PerformanceUtils';
import { getUserIsSuperAdmin } from 'utils/models/User';
import { useFeatures } from 'utils/util/features';
import Page from 'views/Layout/Pages/Page';
import EmptyState from 'views/Widgets/EmptyState';
import * as consts from '../../consts/consts';
import {
  AuthUser,
  Campaign,
  CollapsiblePhaseConfig,
  Organization,
  ReduxState,
} from '../../types';
import { useConfirmApi } from '../../utils/api/ApiHooks';
import {
  CAMPAIGN_PHASE_TOKEN_VALIDITY_HOURS,
  hasPhase,
} from '../../utils/models/Campaign';
import { getPrettyDate, simpleHash } from '../../utils/util/util';
import CardHeaderTitle from '../Widgets/Cards/CardHeaderTitle';
import Loading from '../Widgets/Loading';
import { PropsListOptions } from './CampaignFlow/common';
import {
  getCampaignPropsListOptions,
  getPropsList,
} from './CampaignFlow/PerformancePhasePropsList';

const CopyToBufferButton = ({
  value,
  link,
  copyLink,
}: {
  value: string;
  link: string;
  copyLink: (link: string) => void;
}) => (
  <Button
    color="btn btn-sm btn-light ms-2"
    aria-label={value}
    onClick={() => copyLink(process.env.REACT_APP_CONFIRM_APP_URL + link)}
  >
    <i className={`m-0 p-0 me-2 bi ${consts.ICONS.COPY}`} />
    <FormattedMessage
      id="app.views.administration.campaign_secret_links.button.copy_link_to_clipboard"
      defaultMessage="Copy link to the clipboard"
    />
  </Button>
);

const getSecretLink = (
  phaseName: string | ReactNode,
  link: string,
  copyLink: (link: string) => void,
  className?: string
) => {
  return (
    <li className={className ?? 'mb-2'}>
      <FormattedMessage
        id="app.views.administration.campaign_secret_links.phase_link"
        defaultMessage="{phaseName}: <chunk>Copy Link</chunk>"
        values={{
          phaseName,
          chunk: (value) => (
            <>
              <Link to={link} target="_blank" rel="noopener noreferrer">
                <FormattedMessage
                  id="app.views.administration.campaign_secret_links.link.text"
                  defaultMessage="Link"
                />
              </Link>
              <CopyToBufferButton
                value={String(value)}
                link={link}
                copyLink={copyLink}
              />
            </>
          ),
        }}
      />
    </li>
  );
};

interface Props {
  campaign: Campaign;
  currentOrganization: Organization;
}

interface CampaignTokens {
  campaign_tokens: {
    S: string;
    O: string;
    E: string;
  };
}

const getPhaseTitle = (
  phase: string,
  configs: CollapsiblePhaseConfig[]
): ReactNode | undefined => {
  const config = configs.find((c) => c.type === phase);
  return config?.title;
};

const CampaignSecretLinks: FC<Props> = ({ campaign, currentOrganization }) => {
  const { formatMessage, locale } = useIntl();
  const authUser = useSelector<ReduxState, AuthUser | undefined>(
    (state) => state?.authUser
  );
  const { features, isEnabled } = useFeatures();
  // we create a random id for tracking purposes (to give users a sense of security
  // that they can track uniquely who used a given link)

  const { data, status, error } = useConfirmApi<CampaignTokens>({
    url: '/get-campaign-tokens',
    method: 'POST',
    params: {
      campaign_id: campaign.id,
      organization_id: currentOrganization.id,
    },
  });
  const campaignHash = simpleHash(campaign.id);

  const isSuperAdmin = getUserIsSuperAdmin(authUser);

  const wizardType = getWizardType(campaign);
  const options: PropsListOptions = getCampaignPropsListOptions(
    wizardType,
    isSuperAdmin,
    features,
    true
  );

  const propsList = getPropsList(
    formatMessage,
    options,
    wizardType,
    features,
    campaign
  );

  const copyLink = useCallback(
    (link) => {
      navigator.clipboard.writeText(link);
      toast.success(
        formatMessage({
          id: 'app.views.referrals.referrals.invite_via_link.copy_successful',
          defaultMessage: 'The link was copied to the clipboard.',
        })
      );
    },
    [formatMessage]
  );

  // the survey is the whole Engagement/eNPS cycle, and without Engagement it
  // can't be managed, so there's no phase to hand out access to
  if (
    ENGAGEMENT_ENTITLEMENT_WIZARD_TYPES.includes(wizardType) &&
    !isEnabled('engagement')
  ) {
    return (
      <Page embedded={true} wrapBodyInCard={true}>
        <EmptyState
          title={formatMessage({
            id: 'app.views.administration.campaign_secret_links.title.there_are_no_secret_links_available_for_this_cycle',
            defaultMessage:
              'There are no secret links available for this cycle.',
          })}
          subtitle={formatMessage({
            id: 'app.views.administration.campaign_secret_links.subtitle.engagement_is_not_enabled_for_your_organization',
            defaultMessage: 'Engagement is not enabled for your organization.',
          })}
        />
      </Page>
    );
  }

  if (status === 'LOADING') {
    return (
      <Loading
        message={formatMessage({
          id: 'app.views.administration.campaign_secret_links.message.generating_secret_links',
          defaultMessage: 'Generating secret links',
        })}
      />
    );
  }

  if (status === 'ERROR') {
    console.warn('Error generating secret links', JSON.stringify(error));
    return (
      <Card>
        <CardBody>
          <FormattedMessage
            id="app.views.administration.campaign_secret_links.message.generating_secret_links.error"
            defaultMessage="Error generating secret links"
          />
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardHeaderTitle>
          <FormattedMessage
            id="app.views.administration.campaign_secret_links.header"
            defaultMessage="Secret links"
          />
        </CardHeaderTitle>
      </CardHeader>
      <CardBody>
        <p className="mb-4">
          <FormattedMessage
            id="app.views.administration.campaign_secret_links.paragraph.give_acees"
            defaultMessage="
            To give someone access to a specific phase before it opens or after it closes, you can give them a secret link:
            "
          />
        </p>

        <ul>
          {hasPhase(campaign, PHASE_TYPE_SELF) &&
            getSecretLink(
              getPhaseTitle(PHASE_TYPE_SELF, propsList),
              (getCampaignHasFeatureEnabledDefaultOn(
                campaign,
                PERFORMANCE_FEATURE_INCLUDE_BASICS_PAGE
              )
                ? consts.PERFORMANCE_STEP_BASICS(formatMessage, false).path
                : consts.PERFORMANCE_STEP_WELCOME(formatMessage, false).path) +
                `?access_id=${data?.campaign_tokens?.S}&ref=${campaignHash}`,
              copyLink
            )}
          {hasPhase(campaign, PHASE_TYPE_OTHERS) &&
            getSecretLink(
              getPhaseTitle(PHASE_TYPE_OTHERS, propsList),
              consts.PERFORMANCE_STEP_OTHERS_PHASE_WELCOME(formatMessage, false)
                .path +
                `?access_id=${data?.campaign_tokens?.O}&ref=${campaignHash}`,
              copyLink
            )}
          {hasPhase(campaign, PHASE_TYPE_EVALUATION) &&
            getSecretLink(
              getPhaseTitle(PHASE_TYPE_EVALUATION, propsList),
              consts.PERFORMANCE_STEP_ASSESS_DIRECT_REPORTS(
                formatMessage,
                false
              ).path +
                `?access_id=${data?.campaign_tokens?.E}&ref=${campaignHash}`,
              copyLink
            )}
        </ul>

        <p>
          <FormattedMessage
            id="app.views.administration.campaign_secret_links.paragraph.expiration"
            defaultMessage="
            NOTE: These links are auto-generated and can be shared with one or more people.
            The links are valid for <b>{numOfHours} hours</b> from the time of generation.
            Links are generated when this page is loaded. The current link above will expire on <b>{expiryDate}</b>.
            Please refresh this page if you need to generate new links."
            values={{
              b: (chunks) => <b>{chunks}</b>,
              numOfHours: CAMPAIGN_PHASE_TOKEN_VALIDITY_HOURS,
              expiryDate: getPrettyDate({
                dateString: add(new Date(), {
                  hours: CAMPAIGN_PHASE_TOKEN_VALIDITY_HOURS,
                }),
                locale,
                includeTime: true,
              }),
            }}
          />
        </p>
        <p className="mb-0">
          <FormattedMessage
            id="app.views.administration.campaign_secret_links.paragraph.help_provide"
            defaultMessage="
          We can provide usage information of these links upon request.
        "
          />
        </p>
      </CardBody>
    </Card>
  );
};

const mapStateToProps = (state: ReduxState) => {
  const { currentOrganization } = state;

  return {
    currentOrganization,
  };
};

export default connect(mapStateToProps)(React.memo(CampaignSecretLinks));
